// Frozen contract for the Adaptive Sampling feature. Vocabulary is deliberately generic; no algorithm detail.

export type AxisSpec = {
  name: string;
  units: string;
  mode: 'range' | 'fixed';
  min?: number;
  max?: number;
  fixedValue?: number;
};

export type OutputSpec = {
  name: string;
  weight: number;
  targetSigma?: number;
};

export type SolverChoice = 'AVL' | 'JVL' | 'DATCOM' | 'panel' | 'Flow360';

export type OracleSpec = {
  enabled: boolean;
  datasetId?: string;
};

export type CampaignConfig = {
  name: string;
  inputs: AxisSpec[];
  outputs: OutputSpec[];
  budget: number;
  batchSize: number;
  lowFiSolver: SolverChoice;
  highFiSolver: SolverChoice;
  computePool: string;
  flexCreditsPerHighFiRun: number;
  stopEarlyIfTargetsMet: boolean;
  oracle: OracleSpec;
};

export type SamplePoint = {
  id: string;
  iter: number;
  batchId: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  source: 'sampler' | 'manual';
  inputs: Record<string, number>;
  outputs?: Record<string, number>;
  truthOutputs?: Record<string, number>;
  predicted?: Record<string, { mean: number; sigma: number }>;
  runtimeSec?: number;
  costFlexCredits?: number;
  flow360CaseUrl?: string;
};

export type CampaignMetrics = {
  perOutput: Record<string, {
    maxSigmaByIter: number[];
    meanSigmaByIter: number[];
    realRmseByIter?: number[];
    currentMaxSigma: number;
    currentMeanSigma: number;
    currentRealRmse?: number;
    targetSigma?: number;
    calibration?: Array<{ predictedSigma: number; realisedAbsError: number }>;
  }>;
  cumulativeCostFlexCredits: number;
  estimatedCompletionIso?: string;
  autonomousRunCount: number;
  manualRunCount: number;
};

export type CampaignState = {
  id: string;
  config: CampaignConfig;
  status: 'configuring' | 'running' | 'paused' | 'done' | 'failed';
  iteration: number;
  pointsComplete: number;
  pointsInFlight: number;
  points: SamplePoint[];
  metrics: CampaignMetrics;
  createdAt: string;
  updatedAt: string;
};

export type SliceState = {
  xInput: string;
  yInput: string;
  pinned: Record<string, number>;
  slabFraction: number;
};

export type SurfaceResponse = {
  x: number[];
  y: number[];
  mean: number[][];
  sigma: number[][];
  truthMean?: number[][];
};

export type ScoreResponse = {
  x: number[];
  y: number[];
  score: number[][];
  topK: Array<{ x: number; y: number; rank: number }>;
};

export type OracleAxis = { name: string; min: number; max: number; units: string };

export type OracleOutputMapping = {
  canonicalName: string;
  oracleColumnName: string;
  available: boolean;
};

export type OracleDataset = {
  id: string;
  label: string;
  axes: OracleAxis[];
  outputMappings: OracleOutputMapping[];
  pointCount: number;
  notes?: string;
};

// ---------------------------------------------------------------------------
// Shared vocabulary — canonical input/output names and defaults.
// These are the only names other workers should reference when constructing
// AxisSpec / OutputSpec objects for the demo campaigns.
// ---------------------------------------------------------------------------

export type CanonicalInputName =
  | 'alpha' | 'beta' | 'Mach' | 'Re' | 'RPM'
  | 'RPM_1' | 'RPM_2' | 'RPM_3' | 'RPM_4' | 'RPM_5' | 'RPM_6' | 'RPM_7' | 'RPM_8'
  | 'flap' | 'aileron' | 'elevator' | 'rudder' | 'thrust';

export type CanonicalOutputName = 'CL' | 'CD' | 'CY' | 'Cmx' | 'Cmy' | 'Cmz';

export const CANONICAL_OUTPUTS: readonly CanonicalOutputName[] = [
  'CL', 'CD', 'CY', 'Cmx', 'Cmy', 'Cmz',
] as const;

export const CANONICAL_INPUT_DEFAULTS: Record<
  CanonicalInputName,
  { units: string; defaultRange?: [number, number]; defaultFixed?: number }
> = {
  alpha:    { units: 'deg',           defaultRange: [-5, 20] },
  beta:     { units: 'deg',           defaultRange: [-10, 10] },
  Mach:     { units: 'dimensionless', defaultRange: [0.1, 0.8] },
  Re:       { units: 'dimensionless', defaultRange: [5e5, 5e7] },
  RPM:      { units: 'rpm',           defaultRange: [0, 6000] },
  RPM_1:    { units: 'rpm',           defaultRange: [0, 6000] },
  RPM_2:    { units: 'rpm',           defaultRange: [0, 6000] },
  RPM_3:    { units: 'rpm',           defaultRange: [0, 6000] },
  RPM_4:    { units: 'rpm',           defaultRange: [0, 6000] },
  RPM_5:    { units: 'rpm',           defaultRange: [0, 6000] },
  RPM_6:    { units: 'rpm',           defaultRange: [0, 6000] },
  RPM_7:    { units: 'rpm',           defaultRange: [0, 6000] },
  RPM_8:    { units: 'rpm',           defaultRange: [0, 6000] },
  flap:     { units: 'deg',           defaultRange: [-20, 20] },
  aileron:  { units: 'deg',           defaultRange: [-20, 20] },
  elevator: { units: 'deg',           defaultRange: [-20, 20] },
  rudder:   { units: 'deg',           defaultRange: [-20, 20] },
  thrust:   { units: 'N',             defaultRange: [0, 1000] },
};

export type ControlAction = 'pause' | 'resume' | 'stop';

export type WebSocketEvent =
  | { type: 'state';     campaignId: string; state: CampaignState }
  | { type: 'point';     campaignId: string; point: SamplePoint }
  | { type: 'batch';     campaignId: string; iteration: number; pointIds: string[] }
  | { type: 'metrics';   campaignId: string; metrics: CampaignMetrics }
  | { type: 'completed'; campaignId: string }
  | { type: 'error';     campaignId: string; message: string };

export const FEATURE_FLAG_ORACLE_MODE = 'adaptive_sampling_oracle_mode' as const;
